import axios from "axios";

import type { QueryKey, QueryObserverOptions } from "@tanstack/query-core";
import type { Axios } from "axios";

const { localStorage } = global.window;

export interface APIConf {
  token: string;
  baseURL?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestFunctionList?: RequestFunction<unknown, any[]>[];
}

export interface Query<TData = unknown, TQueryFnData = TData> {
  queryKey: QueryKey;
  queryFn: () => Promise<TQueryFnData>;
  options?: QueryObserverOptions<TQueryFnData, unknown, TData, TQueryFnData>;
}

type QueryGenerator = (...args: unknown[]) => Query;

export type QueryList = Record<string, QueryGenerator>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestFunction<TResponse, TParams extends any[]> = (
  ...args: TParams
) => Promise<TResponse>;

const axiosInstance = axios.create({
  baseURL:
    process.env.NODE_ENV !== "production" && process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : window.location.origin,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface GenerateQueryParams<TResponse, TParams extends any[]> {
  fn: RequestFunction<TResponse, TParams>;
  params: Parameters<RequestFunction<TResponse, TParams>>;
  options?: Omit<
    QueryObserverOptions<TResponse, unknown, TResponse, TResponse>,
    "select"
  >;
}

export class BaseAPI {
  token: string;

  axios: Axios;

  apiBase: string;

  service?: string;

  version?: string;

  constructor(conf: APIConf) {
    this.token = conf.token;

    this.apiBase = "/api";

    this.axios = axiosInstance;

    this.axios.interceptors.request.use((config) => {
      /**
       * Refresh token from local storage
       * Necessary if the user just created an account or just logged in
       * Todo: have a better way of managing the token globally
       * Todo: manage token expiration?
       */

      const updatedConfig = {
        ...config,
        headers: {
          ...config.headers,
          ...(localStorage.token
            ? { Authorization: `Bearer ${localStorage.token}` }
            : {}),
        },
      };

      return updatedConfig;
    });

    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          error.response?.status === 401 &&
          window.location.pathname !== "/login"
        ) {
          window.location.href = "/login?force=true";
        }
        return Promise.reject(error);
      },
    );
  }

  get routeBase(): string {
    if (!this.service || !this.version) {
      throw new Error("Service or version not defined");
    }

    return `${this.apiBase}/${this.service}/${this.version}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  generateQuery<TResponse, TParams extends any[]>({
    fn,
    params,
    options,
  }: GenerateQueryParams<TResponse, TParams>): Query<TResponse, TResponse> {
    if (!fn) {
      throw new Error("Function is required to generateQuery");
    }

    const functionName = fn.name;

    return {
      ...options,
      queryKey: [functionName, ...params],
      queryFn: async () => fn.call(this, ...params),
    };
  }
}

const API = new BaseAPI({
  token: localStorage.token as string,
  baseURL:
    process.env.NODE_ENV !== "production" && process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : window.location.origin,
});

export default API;
